// src/components/personal/sections/ClientsSection/components/StatusSettings/StatusSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  StatusSettingsType, 
  ClientStats, 
  APIError,
  AutoCalculationRequirements
} from '../types/types';
import { TEXT_STYLES } from '@/theme';
import { LOCALES } from '@/utils/constants';
import { formatNumber } from '@/utils/Formatters';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Layout';
import { Flex } from '@/components/ui/Layout';
import { COMPONENT_STYLES } from '@/theme/components';
import Message from '@/components/ui/Message';
import { useResponsive } from '@/hooks/useResponsive';

// Объявление метода confirm для глобального объекта window
declare global {
  interface Window {
    confirm(message?: string): boolean;
  }
}

// Стили для скрытия скроллбара (можно вынести в отдельный файл)
const noScrollbarStyles = `
.no-scrollbar {
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */
}
.no-scrollbar::-webkit-scrollbar { 
  display: none;  /* Safari and Chrome */
}
`;

interface StatusSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  settings: StatusSettingsType;
  onSettingsChange: (settings: StatusSettingsType) => void;
  onSave: () => Promise<boolean>;
  stats: ClientStats;
  autoCalculationRequirements: AutoCalculationRequirements;
  error?: APIError | null;
  loading: boolean;
}

const calculateButtonState = (
  isLoading: boolean,
  hasChanges: boolean
): boolean => {
  return isLoading || !hasChanges;
};

// Функция для глубокого сравнения объектов
const isEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  
  if (
    typeof obj1 !== 'object' || 
    obj1 === null || 
    typeof obj2 !== 'object' || 
    obj2 === null
  ) {
    return false;
  }
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    
    if (!isEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
};

export const StatusSettings: React.FC<StatusSettingsProps> = ({
  isOpen,
  onClose,
  settings,
  error,
  loading,
  onSettingsChange,
  onSave}) => {
  const { t } = useTranslation(['clients', 'common']);
  const { isMobile } = useResponsive(); // Добавляем хук для определения мобильного устройства
  
  const [localSettings, setLocalSettings] = useState<StatusSettingsType>({...settings});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveAttempted, setSaveAttempted] = useState<boolean>(false);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Получаем значения средних интервалов из average_intervals
  const averageLoyalInterval = settings.average_intervals?.average_loyal_interval || 0;
  const averageRegularInterval = settings.average_intervals?.average_regular_interval || 0;

  // Обновляем локальные настройки при открытии модального окна или изменении настроек
  useEffect(() => {
    if (isOpen && settings) {
      setLocalSettings({...settings});
      setHasChanges(false);
      setIsSaving(false);
      setSaveAttempted(false);
    }
  }, [isOpen, settings]);
  
  // Очистка таймеров при размонтировании компонента
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);
  
  // Проверяем наличие изменений при обновлении локальных настроек
  useEffect(() => {
    if (settings && localSettings) {
      // Сравниваем только нужные поля настроек
      const settingsToCompare = {
        ordersX: settings.ordersX,
        ordersZ: settings.ordersZ,
        intervalA: settings.intervalA,
        intervalB: settings.intervalB,
        daysC: settings.daysC
      };
      
      const localSettingsToCompare = {
        ordersX: localSettings.ordersX,
        ordersZ: localSettings.ordersZ,
        intervalA: localSettings.intervalA,
        intervalB: localSettings.intervalB,
        daysC: localSettings.daysC
      };
      
      const changed = !isEqual(localSettingsToCompare, settingsToCompare);
      setHasChanges(changed);
    }
  }, [localSettings, settings]);

  const handleSafeClose = useCallback(() => {
    // Проверяем есть ли несохраненные изменения и не выполняется ли сохранение
    if (isSaving) {
      return; // Не закрываем во время сохранения
    }
    
    // Очищаем таймеры если они есть
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      setSaveTimeout(null);
    }
    
    // Skip confirm dialog if no changes, or show it and check result
    if (!hasChanges || window.confirm(t('settings.unsaved_changes_confirm'))) {
      onClose();
    }
  }, [hasChanges, onClose, isSaving, t, saveTimeout]);

  const handleSettingChange = useCallback((
    field: keyof Omit<StatusSettingsType, 'id' | 'business' | 'created_at' | 'updated_at' | 'average_intervals'>,
    value: number
  ) => {
    setLocalSettings(prev => {
      return {
        ...prev,
        [field]: Math.max(0, value)
      };
    });
  }, []);

  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Omit<StatusSettingsType, 'id' | 'business' | 'created_at' | 'updated_at' | 'average_intervals'>
  ) => {
    const inputValue = (e.target as any).value || '';
    const value = Math.max(0, parseInt(inputValue, 10));
    handleSettingChange(field, isNaN(value) ? 0 : value);
  }, [handleSettingChange]);

  const handleSave = async () => {
    if (isSaving || !hasChanges || saveAttempted) return;

    setSaveAttempted(true);
    setIsSaving(true);
    
    // НОВОЕ - устанавливаем таймер для защиты от длительных операций
    const timeoutId = setTimeout(() => {
      console.log('Превышено время ожидания сохранения настроек (5 секунд)');
      // Закрываем модальное окно принудительно
      setIsSaving(false);
      setSaveAttempted(false);
      onClose();
    }, 5000); // 5 секунд вместо минуты
    
    setSaveTimeout(timeoutId);

    
    try {
      // Подготавливаем только те данные, которые нужны для отправки
      const settingsToSave = {
        ...settings, // Сохраняем базовые данные (id, business и т.д.)
        ordersX: localSettings.ordersX,
        ordersZ: localSettings.ordersZ,
        intervalA: localSettings.intervalA,
        intervalB: localSettings.intervalB,
        daysC: localSettings.daysC
      };
      
      // Передаем только измененные настройки родителю
      onSettingsChange(settingsToSave);
      
      // Вызываем API через родителя
      const success = await onSave();
      
      // Очищаем таймер
      if (saveTimeout) {
        clearTimeout(saveTimeout);
        setSaveTimeout(null);
      }
      
      if (success) {
        setHasChanges(false);
        // Закрываем модальное окно после успешного сохранения
        onClose();
      }
    } catch (err) {
      console.error(t('settings.save_error'), err);
      // Очищаем таймер при ошибке
      if (saveTimeout) {
        clearTimeout(saveTimeout);
        setSaveTimeout(null);
      }
    } finally {
      setIsSaving(false);
      // Через небольшую задержку сбрасываем флаг, чтобы разрешить повторные нажатия
      setTimeout(() => {
        setSaveAttempted(false);
      }, 500);
    }
  };

  const isButtonDisabled = calculateButtonState(
    isSaving || loading || saveAttempted, 
    hasChanges
  );

  const smallInputClass = `${COMPONENT_STYLES.input.base} ${COMPONENT_STYLES.input.sizes.sm} w-16 text-center`;

  return (
    <>
      {/* Добавляем стили для скрытия скроллбара и безопасной области */}
      <style>{`
        ${noScrollbarStyles}
        
        .safe-area-padding {
          padding-top: env(safe-area-inset-top, 20px);
        }
        
        @media (max-width: 640px) {
          .modal-close-button {
            width: 44px !important;
            height: 44px !important;
            top: env(safe-area-inset-top, 10px);
            right: 10px;
          }
        }
      `}</style>

      <Modal
        isOpen={isOpen}
        onClose={handleSafeClose}
        size={isMobile ? "full" : "2xl"}
        mobileMode={isMobile ? "fullscreen" : "normal"}
        draggable={!isMobile}
        swipeToDismiss={isMobile}
        className={isMobile ? "safe-area-padding" : ""}
        heightType="auto"
        allowBackdropClose={!hasChanges}
      >
        <ModalHeader 
          onClose={handleSafeClose} 
          className={isMobile ? "pt-8 sm:pt-4" : ""}
        >
          {t('settings.title')}
        </ModalHeader>

        <ModalBody scrollable={true}>
          {loading ? (
            <div className="flex justify-center items-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2">{t('common:loading')}</span>
            </div>
          ) : error ? (
            <Message 
              type="error" 
              message={error.message}
              translateKey="errors.data_load_error"
            />
          ) : (
            <div>
              <h3 className={`${TEXT_STYLES.h3} mb-2 text-sm`}>{t('settings.settings_title')}</h3>

              {/* Rare and Regular Client Settings */}
              <div className="mb-1">
                <div className="flex flex-nowrap justify-between">
                  <div className="flex items-center">
                    <label htmlFor="ordersX" className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                      {t('settings.fields.orders_x_label')}
                    </label>
                    <input
                      id="ordersX"
                      type="number"
                      value={localSettings.ordersX}
                      onChange={(e) => handleInputChange(e, 'ordersX')}
                      disabled={loading}
                      min="0"
                      max="999"
                      className={smallInputClass}
                      aria-label={t('settings.fields.orders_x_aria')}
                    />
                  </div>
                  <div className="invisible" aria-hidden="true">
                    {/* Пустой элемент для выравнивания */}
                  </div>
                </div>
              </div>

              <div className="mb-1">
                <div className="flex flex-nowrap justify-between">
                  <div className="flex items-center">
                    <label htmlFor="ordersZ" className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                      {t('settings.fields.orders_z_label')}
                    </label>
                    <input
                      id="ordersZ"
                      type="number"
                      value={localSettings.ordersZ}
                      onChange={(e) => handleInputChange(e, 'ordersZ')}
                      disabled={loading}
                      min="0"
                      max="999"
                      className={smallInputClass}
                      aria-label={t('settings.fields.orders_z_aria')}
                    />
                  </div>
                  <div className="invisible" aria-hidden="true">
                    {/* Пустой элемент для выравнивания */}
                  </div>
                </div>
              </div>

              <div className="mb-1">
                <div className="flex items-center">
                  <label className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                    {t('settings.fields.interval_header')}
                  </label>
                </div>
                <div className="flex flex-nowrap justify-between">
                  <div className="flex items-center">
                    <label htmlFor="intervalA" className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                      {t('settings.fields.interval_a_label')}
                    </label>
                    <input
                      id="intervalA"
                      type="number"
                      value={localSettings.intervalA}
                      onChange={(e) => handleInputChange(e, 'intervalA')}
                      disabled={loading}
                      min="0"
                      max="999"
                      className={smallInputClass}
                      aria-label={t('settings.fields.interval_a_aria')}
                    />
                  </div>
                  <div className="flex items-center justify-end w-64">
                    <label htmlFor="intervalB" className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                      {t('settings.fields.interval_b_label')}
                    </label>
                    <input
                      id="intervalB"
                      type="number"
                      value={localSettings.intervalB}
                      onChange={(e) => handleInputChange(e, 'intervalB')}
                      disabled={loading}
                      min="0"
                      max="999"
                      className={smallInputClass}
                      aria-label={t('settings.fields.interval_b_aria')}
                    />
                  </div>
                </div>
              </div>
              
              {/* Добавляем поле daysC после intervalA и intervalB */}
              <div className="mb-1">
                <div className="flex flex-nowrap">
                  <div className="flex items-center">
                    <label htmlFor="daysC" className={`${TEXT_STYLES.body} mr-1 whitespace-nowrap`}>
                      {t('settings.fields.days_c_label')}
                    </label>
                    <input
                      id="daysC"
                      type="number"
                      value={localSettings.daysC}
                      onChange={(e) => handleInputChange(e, 'daysC')}
                      disabled={loading}
                      min="0"
                      max="999"
                      className={smallInputClass}
                      aria-label={t('settings.fields.days_c_aria')}
                    />
                  </div>
                </div>
              </div>

              {/* Отображаем средние интервалы с форматированием до 1 знака после запятой */}
              <Card padding="sm" className="mt-3 pt-2 bg-background">
                <div className="mt-2 pt-2 border-t border-secondary/20">
                  <h4 className={`${TEXT_STYLES.body} font-medium mb-2 text-xs`}>
                    {t('settings.intervals_info')}:
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className={`${TEXT_STYLES.small} text-text`}>
                        {t('settings.regular_clients_interval')}:
                      </div>
                      <div className="font-medium text-primary">
                        {t('settings.days_count', { count: Number(formatNumber(averageRegularInterval, 1, false, LOCALES.RU)) })}
                      </div>
                    </div>
                    <div>
                      <div className={`${TEXT_STYLES.small} text-text`}>
                        {t('settings.loyal_clients_interval')}:
                      </div>
                      <div className="font-medium text-primary">
                        {t('settings.days_count', { count: Number(formatNumber(averageLoyalInterval, 1, false, LOCALES.RU)) })}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </ModalBody>

        {/* Показываем кнопки, только если есть изменения */}
        {hasChanges && (
          <ModalFooter mobileFlex="auto">
            <Flex justify="end" gap="sm">
              <button
                type="button"
                onClick={handleSafeClose}
                className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.secondary} ${COMPONENT_STYLES.button.sizes.sm}`}
                disabled={isSaving}
              >
                {t('common:cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isButtonDisabled}
                className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary} ${COMPONENT_STYLES.button.sizes.sm}`}
              >
                {isSaving ? t('common:saving') : t('common:save')}
              </button>
            </Flex>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

export default StatusSettings;